import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Check, Lock, ArrowLeft, Trophy, Sparkles, MessageCircleHeart, HelpCircle, Flame } from 'lucide-react';
import { useCallback } from 'react';

export default function PlanDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [streak, setStreak] = useState(0);

  const load = useCallback(async () => {
    const { data } = await http.get(`/plans/${id}`);
    setPlan(data);
    const { data: enr } = await http.get('/plans/user/enrolled');
    setEnrollment(enr.find(e => e.plan_id === id) || null);
    try {
      const { data: st } = await http.get('/plans/streak');
      setStreak(st.streak || 0);
    } catch { /* streak is non-critical */ }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const enroll = async () => { await http.post(`/plans/${id}/enroll`); toast.success('Enrolled'); load(); };
  const completeDay = async (day) => {
    if (completing) return;
    setCompleting(true);
    try {
      const { data } = await http.post(`/plans/${id}/day/${day}/complete`);
      if (data.just_finished) {
        setCelebrate(true);
      } else if (data.streak_reward) {
        toast.success(`${data.streak_reward}-day streak! Keep the fire going, brother.`, { icon: '🔥' });
      } else {
        toast.success(`Day ${day} complete — nice work.`);
      }
      await load();
    } catch {
      toast.error('Could not mark day complete');
    } finally {
      setCompleting(false);
    }
  };

  if (!plan) return <div className="p-6 text-navy/60">Loading…</div>;
  const locked = plan.premium && !user?.is_premium;
  const done = new Set(enrollment?.completed_days || []);
  const currentDay = enrollment?.current_day || 1;
  const percent = enrollment ? Math.round((done.size / plan.days) * 100) : 0;
  const allDone = enrollment && done.size === plan.days;

  // Strip a leading "Day N:" so the box header isn't repeated in the task text.
  const stripDay = (s) => (s || '').replace(/^\s*Day\s*\d+\s*[:.\-—]\s*/i, '').trim();
  // Deep-link to the AI Coach, pre-asking how to do this exact step.
  const askCoach = (taskText) => {
    const q = `I'm on the "${plan.title}" plan. Today's step is: "${stripDay(taskText)}". Explain simply, step by step, how to actually do this and why it helps.`;
    nav(`/app/coach?prompt=${encodeURIComponent(q)}`);
  };

  // Celebration overlay when the last day was just finished
  if (celebrate) {
    return (
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center p-6 lg:p-10" data-testid="plan-congrats">
        <div className="max-w-lg lg:max-w-2xl w-full bg-white rounded-3xl p-8 lg:p-12 card-soft text-center">
          <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto rounded-full bg-gold/15 flex items-center justify-center mb-5">
            <Trophy size={32} className="text-gold" strokeWidth={1.5}/>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Plan complete</p>
          <h1 className="font-heading text-3xl lg:text-5xl text-navy mt-3">Congratulations, brother.</h1>
          <p className="text-sm lg:text-lg text-navy/70 mt-4 leading-relaxed">
            You just completed <strong>{plan.title}</strong> — {plan.days} days of showing up for yourself. That&apos;s real work. The kind that compounds.
          </p>
          <div className="bg-beige rounded-2xl p-5 lg:p-6 mt-6 text-left">
            <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-2">What&apos;s next</p>
            <ul className="space-y-1.5 text-sm text-navy/75 leading-relaxed">
              <li className="flex gap-2"><span className="text-gold">•</span>Take a beat. Notice how you feel now vs. Day 1.</li>
              <li className="flex gap-2"><span className="text-gold">•</span>Write a short journal reflection — what changed?</li>
              <li className="flex gap-2"><span className="text-gold">•</span>Pick one habit from the plan to keep going.</li>
            </ul>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            <button onClick={() => nav('/app/journal')} data-testid="plan-congrats-journal"
              className="bg-navy text-white rounded-full px-4 py-3 font-medium hover:bg-navy-soft transition-smooth flex items-center justify-center gap-2 text-sm">
              <Sparkles size={14}/> Journal it
            </button>
            <button onClick={() => nav('/app/coach')} data-testid="plan-congrats-coach"
              className="bg-beige text-navy rounded-full px-4 py-3 font-medium hover:bg-[#EAE5D9] transition-smooth flex items-center justify-center gap-2 text-sm">
              <MessageCircleHeart size={14}/> Reflect with Coach
            </button>
            <button onClick={() => { setCelebrate(false); nav('/app/plans'); }} data-testid="plan-congrats-more"
              className="bg-gold text-white rounded-full px-4 py-3 font-medium hover:bg-gold-hover transition-smooth flex items-center justify-center gap-2 text-sm">
              Explore plans
            </button>
          </div>
          <button onClick={() => setCelebrate(false)} data-testid="plan-congrats-close"
            className="mt-5 text-xs text-navy/40 hover:text-navy underline underline-offset-2">
            Back to plan overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-5 lg:space-y-8" data-testid="plan-detail">
      <Link to="/app/plans" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy"><ArrowLeft size={14}/>Back to plans</Link>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{plan.days} days</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-1 lg:mt-2">{plan.title}</h1>
        <p className="text-navy/60 lg:text-lg mt-2 lg:mt-3 max-w-2xl">{plan.description}</p>
      </div>

      {locked ? (
        <div className="bg-white rounded-3xl p-8 card-soft text-center">
          <Lock className="mx-auto text-gold mb-4" size={32} strokeWidth={1.5}/>
          <p className="font-heading text-xl">Members-only plan</p>
        </div>
      ) : !enrollment ? (
        <button data-testid="plan-enroll" onClick={enroll}
          className="w-full lg:w-auto bg-navy text-white rounded-full py-4 lg:py-4 lg:px-10 font-medium hover:bg-navy-soft">Start this plan</button>
      ) : (
        <div className="bg-white rounded-3xl p-5 lg:p-6 card-soft" data-testid="plan-progress">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm lg:text-base font-medium">
              Day <span className="text-navy">{Math.min(currentDay, plan.days)}</span> of {plan.days}
              <span className="text-navy/40"> · {done.size} completed</span>
            </p>
            <p className="text-xs lg:text-sm font-medium text-gold">{percent}%</p>
          </div>
          <div className="h-2 bg-beige rounded-full overflow-hidden">
            <div className="h-full bg-gold transition-all duration-500" style={{ width: `${percent}%` }}/>
          </div>
          {streak > 0 && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs lg:text-sm font-semibold text-gold bg-gold/10 rounded-full px-3 py-1.5" data-testid="plan-streak">
              <Flame size={14} strokeWidth={2}/> {streak}-day streak
            </div>
          )}
          {allDone && (
            <button onClick={() => setCelebrate(true)} data-testid="plan-view-congrats"
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-gold hover:text-gold-hover">
              <Trophy size={14}/> View completion celebration
            </button>
          )}
        </div>
      )}

      {!locked && (
        <>
          <p className="text-xs lg:text-sm text-navy/55 flex items-center gap-1.5 -mb-1">
            <HelpCircle size={14} className="text-gold"/> Not sure how to do a step? Tap <span className="font-medium text-navy/70">&ldquo;How to do this&rdquo;</span> on any day and the Coach will walk you through it.
          </p>
          <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
          {plan.daily.map((d, idx) => {
            const day = idx + 1;
            const isDone = done.has(day);
            const isCurrent = enrollment && day === currentDay && !isDone;
            const isLocked = enrollment && !isDone && day > currentDay;
            return (
              <div key={day} data-testid={`plan-day-${day}`}
                className={`rounded-2xl p-4 lg:p-5 flex flex-col gap-3 transition-smooth ${
                  isDone ? 'bg-green/10 border border-green/20'
                    : isCurrent ? 'bg-white card-soft ring-2 ring-gold/40'
                    : isLocked ? 'bg-beige/40 border border-navy/5'
                    : 'bg-white card-soft'
                }`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    isDone ? 'bg-green text-white' : isCurrent ? 'bg-gold text-white' : isLocked ? 'bg-navy/5 text-navy/40' : 'bg-beige text-navy'
                  }`}>
                    {isDone ? <Check size={16}/> : isLocked ? <Lock size={14}/> : <span className="text-xs font-medium">{day}</span>}
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold mb-1">
                      Day {day}{isCurrent && ' · Today'}{isLocked && ' · Locked'}
                    </p>
                    <p className={`text-sm lg:text-base leading-relaxed ${isLocked ? 'text-navy/40' : 'text-navy/85'}`}>{stripDay(d)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  {isLocked ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-navy/45" data-testid={`plan-day-locked-${day}`}>
                      <Lock size={12}/> Finish Day {currentDay} to unlock
                    </span>
                  ) : (
                    <button
                      onClick={() => askCoach(d)}
                      data-testid={`plan-day-how-${day}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-navy/70 hover:text-navy rounded-full px-3 py-1.5 bg-beige/60 hover:bg-beige transition-smooth"
                    >
                      <HelpCircle size={13}/> How to do this
                    </button>
                  )}
                  {enrollment && !isDone && !isLocked && (
                    <button
                      onClick={() => completeDay(day)}
                      disabled={completing}
                      data-testid={`plan-mark-complete-${day}`}
                      className={`text-xs lg:text-sm font-medium rounded-full px-4 py-2 transition-smooth disabled:opacity-50 ${
                        isCurrent ? 'bg-navy text-white hover:bg-navy-soft' : 'bg-beige text-navy hover:bg-[#EAE5D9]'
                      }`}>
                      Mark day complete
                    </button>
                  )}
                  {isDone && (
                    <span className="text-xs text-green font-medium inline-flex items-center gap-1">
                      <Check size={12}/> Complete
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
}
